import React from "react";
import {
  Trash2, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  RefreshCw, TrendingUp, X, Target, Plus, Pencil
} from "lucide-react";
import SetDataTable from "./SetDataTable";
import ExerciseSearchPanel from "./ExerciseSearchPanel";

const BLOCK_COLORS = [
  { border: "border-l-blue-500",   bg: "bg-blue-500/[0.03]",   headerBg: "bg-blue-500/[0.06]",   dot: "bg-blue-500",   label: "text-blue-400" },
  { border: "border-l-purple-500", bg: "bg-purple-500/[0.03]", headerBg: "bg-purple-500/[0.06]", dot: "bg-purple-500", label: "text-purple-400" },
  { border: "border-l-emerald-500",bg: "bg-emerald-500/[0.03]",headerBg: "bg-emerald-500/[0.06]",dot: "bg-emerald-500",label: "text-emerald-400" },
  { border: "border-l-cyan-500",   bg: "bg-cyan-500/[0.03]",   headerBg: "bg-cyan-500/[0.06]",   dot: "bg-cyan-500",   label: "text-cyan-400" },
  { border: "border-l-rose-500",   bg: "bg-rose-500/[0.03]",   headerBg: "bg-rose-500/[0.06]",   dot: "bg-rose-500",   label: "text-rose-400" },
  { border: "border-l-orange-500", bg: "bg-orange-500/[0.03]", headerBg: "bg-orange-500/[0.06]", dot: "bg-orange-500", label: "text-orange-400" },
];
const CIRCUIT_COLOR = { border: "border-l-amber-400", bg: "bg-amber-500/[0.06]", headerBg: "bg-amber-500/[0.10]", dot: "bg-amber-400", label: "text-amber-400" };

const BlockEditor = ({
  block,
  blockIndex,
  totalBlocks,
  activeLevelIndex,
  onSetActiveLevelTab,
  onMoveBlock,
  onRemoveBlock,
  onToggleCircuit,
  onUpdateBlockName,
  onAddLevel,
  onRemoveLevel,
  onMoveLevel,
  onUpdateLevelField,
  onUpdateSetsCount,
  onUpdateSetData,
  // Exercise search props
  showExerciseSearch,
  replacingExercise,
  searchQuery,
  filteredExercises,
  onOpenSearch,
  onOpenReplace,
  onCloseSearch,
  onSearchChange,
  onSelectExercise,
  onClearExercise,
  // Superset props
  isInSuperset = false,
  onUnlinkFromSuperset,
  onAddExercise,
  // Create & Replace
  onCreateAndReplace,
  onClearAltExercise,
  onClearBasedOff,
  // Batch level field update
  onUpdateMultipleLevelFields,
  // Exercise loading state
  exercisesLoading,
  totalExercises,
  onRetryLoad,
  // Block group props
  exercisePosition = 0,
  totalExercisesInBlock = 1,
  allBlockGroups = [],
  currentBlockGroup = "",
  onMoveToBlock,
  onEditExercise,
  onUpdateCoachNote,
}) => {
  const cli = activeLevelIndex;
  const cl = block.levels[cli];
  const isLast = cli === block.levels.length - 1;
  const isSearchOpenHere = showExerciseSearch?.blockIndex === blockIndex && showExerciseSearch?.levelIndex === cli;

  const isCircuitActive = block.isCircuit || isInSuperset;
  const color = isCircuitActive ? CIRCUIT_COLOR : BLOCK_COLORS[blockIndex % BLOCK_COLORS.length];

  return (
    <div className={`border border-[#1E293B] border-l-4 ${color.border} rounded-xl overflow-hidden ${color.bg}`} data-testid={`block-${blockIndex}`}>
      {/* Exercise Header */}
      <div className={`flex items-center justify-between px-3 py-2 ${color.headerBg} border-b border-[#1E293B]`}>
        <div className="flex items-center gap-2">
          {totalExercisesInBlock > 1 && (
            <div className="flex flex-col gap-0.5">
              <button type="button" onClick={() => onMoveBlock(blockIndex, -1)} disabled={exercisePosition === 0} className={`p-0.5 rounded ${exercisePosition === 0 ? "text-[#475569]" : "text-gray-400 hover:text-white hover:bg-[#2D3B4F]"}`}>
                <ChevronUp className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => onMoveBlock(blockIndex, 1)} disabled={exercisePosition === totalExercisesInBlock - 1} className={`p-0.5 rounded ${exercisePosition === totalExercisesInBlock - 1 ? "text-[#475569]" : "text-gray-400 hover:text-white hover:bg-[#2D3B4F]"}`}>
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <span className={`w-2 h-2 rounded-full ${color.dot} flex-shrink-0`} />
          <span className="text-sm font-semibold text-white">{cl?.exercise_name || `Exercise ${exercisePosition + 1}`}</span>
          {cl?.exercise_id && (
            <button
              type="button"
              onClick={() => onEditExercise?.(cl.exercise_id, cl.exercise_name)}
              className="p-1 text-gray-400 hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
              title="Edit exercise details"
              data-testid={`edit-exercise-${blockIndex}`}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          <span className="text-[10px] text-gray-500">{block.levels.length} level{block.levels.length !== 1 ? "s" : ""}</span>
          {allBlockGroups.length > 1 && (
            <select
              value={currentBlockGroup}
              onChange={(e) => onMoveToBlock?.(blockIndex, e.target.value)}
              className="bg-[#0B0F1A] border border-[#2D3B4F] rounded px-1.5 py-0.5 text-[10px] text-gray-400 cursor-pointer outline-none ml-1"
              title="Move to block"
              data-testid={`move-to-block-${blockIndex}`}
            >
              {allBlockGroups.map(g => <option key={g} value={g}>Block {g}</option>)}
            </select>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={() => onAddLevel(blockIndex)} className="flex items-center gap-1 bg-emerald-600/20 text-emerald-400 px-2 py-1 rounded-lg text-[11px] font-medium hover:bg-emerald-600/30" data-testid={`add-level-${blockIndex}`}>
            <TrendingUp className="w-3 h-3" /> Level
          </button>
          <button type="button" onClick={() => onRemoveBlock(blockIndex)} className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors" data-testid={`remove-block-${blockIndex}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Coach's Note */}
      {exercisePosition === 0 && (
        <div className="px-4 pt-3">
          <textarea
            value={block.coach_note || ""}
            onChange={(e) => onUpdateCoachNote?.(blockIndex, e.target.value)}
            placeholder="Coach's note for this block... (athletes must acknowledge before starting)"
            className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-gray-300 placeholder-gray-600 outline-none focus:border-amber-500/50 resize-none"
            rows={1}
            onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
            data-testid={`coach-note-${blockIndex}`}
          />
        </div>
      )}

      {/* Level Tabs */}
      {block.levels.length > 1 && (
        <div className="flex items-center gap-1 px-4 pt-3 pb-0 flex-wrap">
          {block.levels.map((level, li) => (
            <div key={li} className="flex items-center gap-0">
              {cli === li && li > 0 && (
                <button
                  type="button"
                  onClick={() => onMoveLevel(blockIndex, li, -1)}
                  className="p-1 text-gray-500 hover:text-white hover:bg-[#2D3B4F] rounded-l transition-colors"
                  data-testid={`move-level-left-${blockIndex}-${li}`}
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => onSetActiveLevelTab(blockIndex, li)}
                className={`px-3 py-1.5 rounded-t text-xs font-medium transition-colors ${
                  cli === li
                    ? "bg-emerald-600 text-white"
                    : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F] hover:text-white"
                }`}
                data-testid={`block-${blockIndex}-level-tab-${li}`}
              >
                {level.name || `Level ${level.level}`}
              </button>
              {cli === li && li < block.levels.length - 1 && (
                <button
                  type="button"
                  onClick={() => onMoveLevel(blockIndex, li, 1)}
                  className="p-1 text-gray-500 hover:text-white hover:bg-[#2D3B4F] rounded-r transition-colors"
                  data-testid={`move-level-right-${blockIndex}-${li}`}
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Active Level Content */}
      {cl && (
        <div className="p-4">
          {block.levels.length > 1 && (
            <div className="flex items-center justify-between mb-3 gap-3">
              <div className="flex items-center gap-2 flex-1">
                <span className="text-xs font-bold text-emerald-400 whitespace-nowrap">Level {cl.level}</span>
                <input
                  type="text"
                  value={cl.name || ""}
                  onChange={(e) => onUpdateLevelField(blockIndex, cli, "name", e.target.value)}
                  placeholder={`Level ${cl.level} name (optional)`}
                  className="flex-1 bg-[#0B0F1A] border border-[#1E293B] rounded px-2 py-1 text-xs text-white focus:border-emerald-500 outline-none placeholder:text-gray-600"
                  data-testid={`level-name-${blockIndex}-${cli}`}
                />
              </div>
              <button
                type="button"
                onClick={() => onRemoveLevel(blockIndex, cli)}
                className="text-red-400 hover:text-red-300 text-xs flex items-center gap-1 whitespace-nowrap"
              >
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            </div>
          )}

          {cl.exercise_name && !isSearchOpenHere ? (
            /* Exercise Selected View */
            <div className="bg-[#141B2D] rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-medium text-white">{cl.exercise_name}</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onOpenReplace(blockIndex, cli)}
                    className="flex items-center gap-1.5 px-2 py-1 bg-[#2D3B4F] hover:bg-amber-500/20 hover:text-amber-400 rounded text-sm text-gray-400 transition-colors"
                    data-testid={`replace-exercise-${blockIndex}`}
                  >
                    <RefreshCw className="w-3.5 h-3.5" /> Replace
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (onCreateAndReplace) onCreateAndReplace(blockIndex, cli);
                    }}
                    className="flex items-center gap-1.5 px-2 py-1 bg-[#2D3B4F] hover:bg-green-500/20 hover:text-green-400 rounded text-sm text-gray-400 transition-colors"
                    data-testid={`create-replace-exercise-${blockIndex}`}
                  >
                    <Plus className="w-3.5 h-3.5" /> Create & Replace
                  </button>
                  <button
                    type="button"
                    onClick={() => onClearExercise(blockIndex, cli)}
                    className="p-1 hover:bg-[#2D3B4F] rounded text-red-400"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Advancement Criteria (multi-level blocks only) */}
              {block.levels.length > 1 && (
                <div className="space-y-2 mb-3">
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-gray-500">RM / Max Type</label>
                      <input
                        type="text"
                        value={cl.rm_type}
                        onChange={(e) => onUpdateLevelField(blockIndex, cli, "rm_type", e.target.value)}
                        placeholder="e.g. 5RM, Speed"
                        className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded px-2 py-1.5 text-sm text-white"
                        data-testid={`block-${blockIndex}-level-${cli}-rm`}
                      />
                    </div>
                    {!isLast && (
                      <>
                        <div>
                          <label className="text-xs text-gray-500">Advance By</label>
                          <select
                            value={cl.advancement_type || "bw_multiplier"}
                            onChange={(e) => {
                              const newType = e.target.value;
                              const updates = { advancement_type: newType, advancement_value: "" };
                              if (newType === "bw_multiplier") { updates.bw_multiplier = ""; }
                              onUpdateMultipleLevelFields(blockIndex, cli, updates);
                            }}
                            className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded px-2 py-1.5 text-sm text-white"
                            data-testid={`block-${blockIndex}-level-${cli}-adv-type`}
                          >
                            <option value="bw_multiplier">Relative (×BW)</option>
                            <option value="absolute_weight">Absolute (lbs)</option>
                            <option value="speed">Speed / Time</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">
                            {(cl.advancement_type || "bw_multiplier") === "bw_multiplier" ? "×BW to advance" :
                             cl.advancement_type === "absolute_weight" ? "Weight (lbs)" : "Target value"}
                          </label>
                          <input
                            type="number"
                            step={cl.advancement_type === "bw_multiplier" || !cl.advancement_type ? "0.05" : "1"}
                            min="0"
                            value={cl.advancement_value || cl.bw_multiplier || ""}
                            onChange={(e) => {
                              const val = e.target.value ? parseFloat(e.target.value) : "";
                              const updates = { advancement_value: val };
                              if ((cl.advancement_type || "bw_multiplier") === "bw_multiplier") {
                                updates.bw_multiplier = val;
                              }
                              onUpdateMultipleLevelFields(blockIndex, cli, updates);
                            }}
                            placeholder={cl.advancement_type === "absolute_weight" ? "45" : cl.advancement_type === "speed" ? "12" : "1.0"}
                            className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded px-2 py-1.5 text-sm text-white"
                            data-testid={`block-${blockIndex}-level-${cli}-adv-value`}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Based Off Exercise */}
              <div className="mb-3">
                <label className="text-xs text-gray-500 mb-1 block">% Based off of</label>
                {cl.based_off_exercise_id && cl.based_off_exercise_id !== cl.exercise_id ? (
                  <div className="flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/20 rounded-lg px-3 py-2">
                    <Target className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
                    <span className="text-sm text-cyan-400 font-medium flex-1 truncate">{cl.based_off_exercise_name}</span>
                    <button type="button"
                      onClick={() => onOpenSearch(blockIndex, cli, "based_off")}
                      className="text-xs text-cyan-400 hover:text-cyan-300 font-medium"
                    >Change</button>
                    <button type="button" onClick={() => onClearBasedOff(blockIndex, cli)}
                      className="p-0.5 text-cyan-400/50 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-3 py-2">
                    <span className="text-xs text-gray-500 flex-1">Same exercise (self)</span>
                    <button type="button"
                      onClick={() => onOpenSearch(blockIndex, cli, "based_off")}
                      className="text-xs text-cyan-400 hover:text-cyan-300 font-medium"
                      data-testid={`based-off-btn-${blockIndex}-${cli}`}
                    >Change</button>
                  </div>
                )}
              </div>

              {/* Alternative Exercise */}
              <div className="mb-3">
                <label className="text-xs text-gray-500 mb-1 block">Alternative Exercise</label>
                {cl.alt_exercise_id ? (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <RefreshCw className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                      <span className="text-sm text-amber-400 font-medium flex-1 truncate">{cl.alt_exercise_name}</span>
                      <button type="button" onClick={() => { onClearAltExercise(blockIndex, cli); }}
                        className="p-0.5 text-amber-400/50 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
                    </div>
                    {cl.alt_sets_data ? (
                      <div className="text-[10px] text-amber-400/60">Custom sets configured ({cl.alt_sets_data.length} sets)</div>
                    ) : (
                      <div className="text-[10px] text-amber-400/60">Uses same sets/reps/% as primary</div>
                    )}
                    <div className="flex gap-2">
                      <button type="button"
                        onClick={() => { if (!cl.alt_sets_data) { onUpdateLevelField(blockIndex, cli, "alt_sets_data", JSON.parse(JSON.stringify(cl.sets_data))); } else { onUpdateLevelField(blockIndex, cli, "alt_sets_data", null); } }}
                        className="text-[10px] px-2 py-1 rounded bg-amber-500/20 text-amber-400 hover:bg-amber-500/30"
                        data-testid={`alt-toggle-custom-${blockIndex}-${cli}`}
                      >{cl.alt_sets_data ? "Use Same Sets" : "Customize Sets"}</button>
                    </div>
                  </div>
                ) : (
                  <button type="button"
                    onClick={() => onOpenSearch(blockIndex, cli, "alt")}
                    className="w-full flex items-center gap-2 bg-[#0B0F1A] border border-[#1E293B] border-dashed rounded-lg px-3 py-2 text-xs text-gray-500 hover:border-amber-500/30 hover:text-amber-400 transition-colors"
                    data-testid={`add-alt-btn-${blockIndex}-${cli}`}
                  >
                    <RefreshCw className="w-3 h-3" /> Add alternative exercise
                  </button>
                )}
              </div>

              {/* Sets Count */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Sets:</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onUpdateSetsCount(blockIndex, cli, -1)}
                    className="w-7 h-7 bg-[#2D3B4F] hover:bg-[#374B69] rounded flex items-center justify-center"
                    data-testid={`sets-minus-${blockIndex}`}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <span className="w-8 text-center font-bold">{cl.sets_data.length}</span>
                  <button
                    type="button"
                    onClick={() => onUpdateSetsCount(blockIndex, cli, 1)}
                    className="w-7 h-7 bg-[#2D3B4F] hover:bg-[#374B69] rounded flex items-center justify-center"
                    data-testid={`sets-plus-${blockIndex}`}
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <SetDataTable
                setsData={cl.sets_data}
                blockIndex={blockIndex}
                levelIndex={cli}
                onUpdateSetData={onUpdateSetData}
              />

              {/* Progression info badges */}
              {block.levels.length > 1 && cl.exercise_name && cl.rm_type && !isLast && (cl.advancement_value || cl.bw_multiplier) && (
                <div className="mt-3 text-xs text-emerald-400/70 bg-emerald-900/10 rounded px-3 py-2">
                  {cl.rm_type} {cl.exercise_name}: advance at {
                    (cl.advancement_type || "bw_multiplier") === "bw_multiplier" ? `${cl.advancement_value || cl.bw_multiplier}× BW` :
                    cl.advancement_type === "absolute_weight" ? `${cl.advancement_value} lbs` :
                    `${cl.advancement_value} (speed)`
                  }
                </div>
              )}
              {block.levels.length > 1 && isLast && cl.exercise_name && (
                <div className="mt-3 text-xs text-amber-400/70 bg-amber-900/10 rounded px-3 py-2">
                  Final level — no further progression
                </div>
              )}
            </div>
          ) : (
            /* No Exercise / Search Panel */
            <ExerciseSearchPanel
              isOpen={isSearchOpenHere}
              searchQuery={searchQuery}
              onSearchChange={onSearchChange}
              filteredExercises={filteredExercises}
              onSelectExercise={onSelectExercise}
              onClose={onCloseSearch}
              isReplacing={!!replacingExercise}
              onAddExerciseClick={() => onOpenSearch(blockIndex, cli)}
              exercisesLoading={exercisesLoading}
              totalExercises={totalExercises}
              onRetryLoad={onRetryLoad}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default BlockEditor;
